import {
  AllURLSearchFilterValues,
  DateRangeSearch,
  DateRangeSearchSchema,
  DateType,
  DateTypeSchema,
  SearchParamsObjSchema,
  SearchTaskAdvanced,
  SearchTaskBasic,
  ShortAppUser,
  SingleAuditLog,
  SingleAuditLogUpdate,
  WithinDate,
  WithinDateSchema,
} from "@/types";
import {
  getNewDayDate,
  getNumArrayFromString,
  getStringArrayFromString,
} from "@/utils/utils";
import {
  addDays,
  addMonths,
  addWeeks,
  subDays,
  subMonths,
  subWeeks,
} from "date-fns";
import Swal from "sweetalert2";

// Union type for both basic and advanced search tasks
export type SearchTaskForFilter = SearchTaskBasic | SearchTaskAdvanced;

// #region extractURLFilters
export function extractURLFilters(
  searchParams: URLSearchParams
): AllURLSearchFilterValues | "error" {
  console.time("extractURLFilters");
  try {
    // title
    const paramTitle = searchParams.get("title") || null;

    // status
    const paramStatus = searchParams.get("status") || null;
    const SearchParamsObjSchemaStatus = SearchParamsObjSchema.shape.status;
    const parmParsedStatus = SearchParamsObjSchemaStatus.safeParse(paramStatus);
    const neatStatus = parmParsedStatus.success ? parmParsedStatus.data : null;

    // collaborators
    const paramCollaborators = searchParams.get("collaborators") || null;
    const neatCollaborators = paramCollaborators
      ? getNumArrayFromString(paramCollaborators).filter(
          (num) => !isNaN(num) && num > 0
        )
      : null;

    // collaboratorSearchType
    const paramCollaboratorSearchType =
      searchParams.get("collaboratorType") || null;
    const SearchParamsObjSchemaCollaboratorSearchType =
      SearchParamsObjSchema.shape.collaboratorSearchType;
    const parsedCollaboratorSearchType =
      SearchParamsObjSchemaCollaboratorSearchType.safeParse(
        paramCollaboratorSearchType
      );
    const neatCollaboratorSearchType = parsedCollaboratorSearchType.success
      ? parsedCollaboratorSearchType.data
      : null;

    // assignedTo
    const paramAssignedTo = searchParams.get("assignedTo") || null;
    const neatAssignedTo = paramAssignedTo
      ? getNumArrayFromString(paramAssignedTo).filter(
          (num) => !isNaN(num) && num > 0
        )
      : null;

    // dueDateType
    const neatDueDateType = getFormattedDateType(
      searchParams.get("dueDateType")
    );

    // dueDateData
    const neatDueDateData = getFormattedDateData(
      searchParams.get("dueDateData"),
      neatDueDateType
    );

    // tagType
    const paramTagType = searchParams.get("tagType") || null;
    const SearchParamsObjSchemaTagType = SearchParamsObjSchema.shape.tagType;
    const parsedTagType = SearchParamsObjSchemaTagType.safeParse(paramTagType);
    const neatTagType = parsedTagType.success ? parsedTagType.data : null;

    // tagData
    const paramTagData = searchParams.get("tagData") || null;
    const neatTagData = paramTagData
      ? getStringArrayFromString(paramTagData).filter(
          (tag) => tag.trim() !== "" && tag !== "undefined"
        )
      : null;

    // createdBy
    const paramCreatedBy = searchParams.get("createdBy") || null;
    const neatCreatedBy = paramCreatedBy
      ? getNumArrayFromString(paramCreatedBy).filter(
          (num) => !isNaN(num) && num > 0
        )
      : null;

    // updatedBy
    const paramUpdatedBy = searchParams.get("updatedBy") || null;
    const neatUpdatedBy = paramUpdatedBy
      ? getNumArrayFromString(paramUpdatedBy).filter(
          (num) => !isNaN(num) && num > 0
        )
      : null;

    // hasUsersTimer
    const paramHasUsersTimer = searchParams.get("hasTimer") || null;
    const neatHasUsersTimer = paramHasUsersTimer
      ? getNumArrayFromString(paramHasUsersTimer).filter(
          (num) => !isNaN(num) && num > 0
        )
      : null;

    // assignedBy
    const paramAssignedBy = searchParams.get("assignedBy") || null;
    const neatAssignedBy = paramAssignedBy
      ? getNumArrayFromString(paramAssignedBy).filter(
          (num) => !isNaN(num) && num > 0
        )
      : null;

    // createdDateType
    const neatCreatedDateType = getFormattedDateType(
      searchParams.get("createdDateType")
    );

    // createdDateData
    const neatCreatedDateData = getFormattedDateData(
      searchParams.get("createdDateData"),
      neatCreatedDateType
    );

    // completedDateType
    const neatCompletedDateType = getFormattedDateType(
      searchParams.get("completedDateType")
    );

    // completedDateData
    const neatCompletedDateData = getFormattedDateData(
      searchParams.get("completedDateData"),
      neatCompletedDateType
    );

    // modifiedDateType
    const neatModifiedDateType = getFormattedDateType(
      searchParams.get("modifiedDateType")
    );

    // modifiedDateData
    const neatModifiedDateData = getFormattedDateData(
      searchParams.get("modifiedDateData"),
      neatModifiedDateType
    );

    const paramInverseFilters = searchParams.get("inverseFilters") || null;
    console.log("paramInverseFilters", paramInverseFilters);
    const arrayInverseFilters = paramInverseFilters
      ? getStringArrayFromString(paramInverseFilters).filter(
          (inverseFilter) =>
            inverseFilter.trim() !== "" && inverseFilter !== "undefined"
        )
      : null;
    const searchParamsObjSchemaInverseFilters =
      SearchParamsObjSchema.shape.inverseFilters;
    const parsedInverseFilters =
      searchParamsObjSchemaInverseFilters.safeParse(arrayInverseFilters);
    const neatInverseFilters = parsedInverseFilters.success
      ? parsedInverseFilters.data
      : null;

    return {
      title: paramTitle,
      status: neatStatus,
      collaborators: neatCollaborators,
      collaboratorSearchType: neatCollaboratorSearchType,
      assignedTo: neatAssignedTo,
      dueDateType: neatDueDateType,
      dueDateData: neatDueDateData,
      tagType: neatTagType,
      tagData: neatTagData,
      createdBy: neatCreatedBy,
      updatedBy: neatUpdatedBy,
      hasUsersTimer: neatHasUsersTimer,
      assignedBy: neatAssignedBy,
      createdDateType: neatCreatedDateType,
      createdDateData: neatCreatedDateData,
      completedDateType: neatCompletedDateType,
      completedDateData: neatCompletedDateData,
      modifiedDateType: neatModifiedDateType,
      modifiedDateData: neatModifiedDateData,
      inverseFilters: neatInverseFilters,
    };
  } catch (error) {
    console.error("Error extracting URL filters:", error);
    return "error";
  } finally {
    console.timeEnd("extractURLFilters");
  }
}
// #endregion extractURLFilters

// #region getFormattedDateData
export function getFormattedDateData(
  param: string | null,
  type: DateType
): WithinDate | DateRangeSearch | Date | null {
  try {
    const dateToday = getNewDayDate(new Date());
    // if (!param) return null;
    if (!type) return null;

    // Does not require param
    if (type === "Any Date") {
      return null;
    } else if (type === "Yesterday") {
      return subDays(dateToday, 1);
    } else if (type === "Today") {
      return dateToday;
    } else if (type === "Tomorrow") {
      return addDays(dateToday, 1);
    }

    if (!param) return null; // param is required for other types

    if (type === "Specific Date") {
      return getNewDayDate(param);
    } else if (type === "Within the last" || type === "Within the next") {
      const parsedDateData = WithinDateSchema.safeParse(JSON.parse(param));
      if (!parsedDateData.success) {
        console.error("Invalid date data:", parsedDateData.error);
        return null;
      }
      return parsedDateData.data;
    } else if (type === "Date Range") {
      const parsedDateData = DateRangeSearchSchema.safeParse(JSON.parse(param));
      if (!parsedDateData.success) {
        console.error("Invalid date data:", parsedDateData.error);
        return null;
      }
      return parsedDateData.data;
    } else {
      console.error("Unknown date type:", type);
      return null;
    }
  } catch (error) {
    console.error("Error parsing date data:", error);
    return null;
  }
}
// #endregion getFormattedDateData

// #region getFormattedDateType
export function getFormattedDateType(param: string | null): DateType | null {
  try {
    if (!param) return null;
    const parsedDateType = DateTypeSchema.safeParse(param);
    return parsedDateType.success ? parsedDateType.data : null;
  } catch (error) {
    console.error("Error parsing date type:", error);
    return null;
  }
}
// #endregion getFormattedDateType

// Filtering
// #region filterSearch

// #region filterSearchTitle
export function filterSearchTitle(
  paramTitle: AllURLSearchFilterValues["title"],
  task: SearchTaskForFilter
): boolean {
  // Filter by title
  // If paramTitle is null or empty, return true (no filter applied)
  if (!paramTitle) {
    return true;
  }
  if (!task.title) {
    return false;
  }

  return task.title.toLowerCase().includes(paramTitle.toLowerCase());
}
// #endregion filterSearchTitle

// #region filterSearchStatus
export function filterSearchStatus(
  paramStatus: AllURLSearchFilterValues["status"],
  task: SearchTaskForFilter
): boolean {
  // Filter by status
  // If paramStatus is null or empty, return true (no filter applied)
  if (!paramStatus) {
    return true;
  }

  if (paramStatus.toLowerCase() === "all") {
    {
      return true;
    } // If paramStatus is "all", return true (no filter applied)
  }

  if (!task.status) {
    return false;
  }

  return task.status.toLowerCase() === paramStatus.toLowerCase();
}
// #endregion filterSearchStatus

// #region filterSearchCollaborators
export function filterSearchCollaborators(
  paramCollaborators: AllURLSearchFilterValues["collaborators"],
  paramCollaboratorsSearchType: AllURLSearchFilterValues["collaboratorSearchType"],
  task: SearchTaskForFilter
): boolean {
  if (!paramCollaborators) {
    return true;
  }
  if (paramCollaborators.length === 0) {
    return true;
  }
  if (!task.collaborators) {
    return false;
  }

  // console.log(paramCollaboratorsSearchType);
  // paramCollaboratorsSearchType: "exact" | "partial" | "any" | null
  if (paramCollaboratorsSearchType === "exact") {
    return (
      paramCollaborators.every((id) =>
        task.collaborators.includes(Number(id))
      ) && paramCollaborators.length === task.collaborators.length
    );
  } else if (paramCollaboratorsSearchType === "partial") {
    // must include all from collaboratorIds and can include more
    return paramCollaborators.every((id) =>
      task.collaborators.includes(Number(id))
    );
  } else if (
    paramCollaboratorsSearchType === "any" ||
    paramCollaboratorsSearchType === null
  ) {
    return task.collaborators.some((collaborator) =>
      paramCollaborators.includes(collaborator)
    );
  } else {
    console.error(
      "Unknown collaborator search type:",
      paramCollaboratorsSearchType
    );
    {
      return false;
    }
  }
}
// #endregion filterSearchCollaborators

// #region filterSearchAssignedTo
export function filterSearchAssignedTo(
  paramAssignedTo: AllURLSearchFilterValues["assignedTo"],
  task: SearchTaskForFilter
): boolean {
  if (!paramAssignedTo) {
    return true;
  }
  if (paramAssignedTo.length === 0) {
    return true;
  }
  if (!task.assignee_id) {
    return false;
  }

  return paramAssignedTo.includes(task.assignee_id);
}
// #endregion filterSearchAssignedTo

// #region filterSearchTags
export function filterSearchTags(
  paramTagData: AllURLSearchFilterValues["tagData"],
  paramTagType: AllURLSearchFilterValues["tagType"],
  task: SearchTaskForFilter
): boolean {
  // if (!paramTagType){return true;}
  if (!paramTagData) {
    return true;
  }
  if (paramTagData.length === 0) {
    return true;
  }
  if (!task.tags || task.tags.length === 0) {
    return false;
  }

  // paramTagType: "exact" | "partial" | "any" | null
  if (paramTagType === "exact") {
    return (
      paramTagData.every((tag) =>
        task.tags.some((t) => t.name.toLowerCase() === tag.toLowerCase())
      ) && paramTagData.length === task.tags.length
    );
  } else if (paramTagType === "partial") {
    // must include all from tagData and can include more
    return paramTagData.every((tag) =>
      task.tags.some((t) => t.name.toLowerCase() === tag.toLowerCase())
    );
  } else if (paramTagType === "any" || paramTagType === null) {
    return task.tags.some((tag) =>
      paramTagData.some(
        (paramTag) => tag.name.toLowerCase() === paramTag.toLowerCase()
      )
    );
  } else {
    console.error("Unknown tag search type:", paramTagType);
    {
      return false;
    }
  }
}
// #endregion filterSearchTags

// #region filterSearchCreatedBy
export function filterSearchCreatedBy(
  paramCreatedBy: AllURLSearchFilterValues["createdBy"],
  task: SearchTaskAdvanced,
  allUsers: ShortAppUser[]
): boolean {
  if (!paramCreatedBy) {
    return true;
  }
  if (paramCreatedBy.length === 0) {
    return true;
  }
  const taskHistory: SingleAuditLog[] = task.history;
  if (!taskHistory || taskHistory.length === 0) {
    return false;
  }
  // find first history item with type "create"
  const createdHistory = taskHistory.find(
    (history) => history.type === "create"
  );

  if (!createdHistory) {
    return false;
  }
  if (!createdHistory.full_name) {
    return false;
  }
  // loop though all users and find the one with the same name as createdHistory.full_name
  const createdByUser = allUsers.find(
    (user) =>
      (user.first_name + " " + user.surname).toLowerCase() ===
      createdHistory.full_name.toLowerCase()
  );
  if (!createdByUser) {
    return false;
  }

  // check if createdByUser.id is in paramCreatedBy
  return paramCreatedBy.includes(createdByUser.id);
}
// #endregion filterSearchCreatedBy

// #region filterSearchUpdatedBy
export function filterSearchUpdatedBy(
  paramUpdatedBy: AllURLSearchFilterValues["updatedBy"],
  task: SearchTaskAdvanced,
  allUsers: ShortAppUser[]
): boolean {
  if (!paramUpdatedBy) {
    return true;
  }
  if (paramUpdatedBy.length === 0) {
    return true;
  }

  if (!allUsers || allUsers.length === 0) {
    console.warn("allUsers is empty");
    return false;
  }

  const taskHistory: SingleAuditLog[] = task.history;
  if (!taskHistory || taskHistory.length === 0) {
    return false;
  }
  // find first history item with type "update"
  const updatedHistory = taskHistory.find(
    (history) => history.type === "update"
  );

  if (!updatedHistory) {
    return false;
  }
  if (!updatedHistory.full_name) {
    return false;
  }
  // loop though all users and find the one with the same name as updatedHistory.full_name
  const updatedByUser = allUsers.find(
    (user) =>
      (user.first_name + " " + user.surname).toLowerCase() ===
      updatedHistory.full_name.toLowerCase()
  );
  if (!updatedByUser) {
    console.warn(
      "User not found for updatedByUser.full_name:",
      updatedHistory.full_name
    );
    return false;
  }

  // check if updatedByUser.id is in paramUpdatedBy
  return paramUpdatedBy.includes(updatedByUser.id);
}

// #endregion filterSearchUpdatedBy

// #region filterSearchAssignedBy
export function filterSearchAssignedBy(
  paramAssignedBy: AllURLSearchFilterValues["assignedBy"],
  task: SearchTaskAdvanced,
  allUsers: ShortAppUser[]
): boolean {
  if (!paramAssignedBy) {
    return true;
  }
  if (paramAssignedBy.length === 0) {
    return true;
  }

  if (!allUsers || allUsers.length === 0) {
    console.warn("allUsers is empty");
    return false;
  }

  const taskHistory: SingleAuditLog[] = task.history;
  if (!taskHistory || taskHistory.length === 0) {
    return false;
  }

  // filter by histories with field "assigned_to"
  const assignedByHistory =
    (taskHistory.filter(
      (history) =>
        history.type === "update" &&
        history.changes.some((change) => change.field === "assigned_to")
    ) as SingleAuditLogUpdate[]) || undefined;

  if (!assignedByHistory || assignedByHistory.length === 0) {
    // No assigned by history found, check the task creator
    const createdByHistory = taskHistory.find(
      (history) => history.type === "create"
    ) as SingleAuditLogUpdate | undefined;
    if (!createdByHistory) {
      return false;
    }

    if (!createdByHistory.full_name) {
      console.warn(
        "full_name missing from createdByHistory.full_name:",
        createdByHistory.full_name
      );
      return false;
    }

    // loop though all users and find the one with the same name as createdByHistory.full_name
    const createdByUser = allUsers.find(
      (user) =>
        (user.first_name + " " + user.surname).toLowerCase() ===
        createdByHistory.full_name.toLowerCase()
    );
    if (!createdByUser) {
      console.warn(
        "User not found for createdByUser.full_name:",
        createdByHistory.full_name
      );
      return false;
    }

    // check if createdByUser.id is in paramAssignedBy
    return paramAssignedBy.includes(createdByUser.id);
  }

  // sort by oldest first
  assignedByHistory.sort((a, b) => {
    const dateA = new Date(a.timestamp);
    const dateB = new Date(b.timestamp);
    return dateA.getTime() - dateB.getTime();
  });

  const assignedBy = assignedByHistory[0].full_name;

  if (!assignedBy) {
    console.warn(
      "full_name missing from assignedByHistory[0].full_name:",
      assignedByHistory[0].full_name
    );
    return false;
  }

  // loop though all users and find the one with the same name as assignedBy
  const assignedByUser = allUsers.find(
    (user) =>
      (user.first_name + " " + user.surname).toLowerCase() ===
      assignedBy.toLowerCase()
  );
  if (!assignedByUser) {
    console.warn(
      "User not found for assignedByUser.full_name:",
      assignedBy.toLowerCase()
    );
    return false;
  }

  // check if assignedByUser.id is in paramAssignedBy
  return paramAssignedBy.includes(assignedByUser.id);
}
// #endregion filterSearchAssignedBy

// #region filterSearchHasUsersTimer
export function filterSearchHasUsersTimer(
  paramHasUsersTimer: AllURLSearchFilterValues["hasUsersTimer"],
  task: SearchTaskAdvanced,
  allUsers: ShortAppUser[]
): boolean {
  if (!paramHasUsersTimer) {
    return true;
  }

  if (paramHasUsersTimer.length === 0) {
    return true;
  }

  if (!allUsers || allUsers.length === 0) {
    console.warn("allUsers is empty");
    return false;
  }

  if (!task.timers) {
    return false;
  }

  // Check if the task has any timers with users
  const hasUsersTimer = task.timers.some((timer) => {
    const timerUser = allUsers.find(
      (user) =>
        `${user.first_name} ${user.surname}`.toLowerCase() ===
        timer.username.toLowerCase()
    );
    return timerUser && paramHasUsersTimer.includes(timerUser.id);
  });

  return hasUsersTimer;
}

// #endregion filterSearchHasUsersTimer

// #region filterSearchDueDate
export function filterSearchDueDate(
  paramDueDateData: AllURLSearchFilterValues["dueDateData"],
  paramDueDateType: AllURLSearchFilterValues["dueDateType"],
  task: SearchTaskForFilter
): boolean {
  if (!paramDueDateType) {
    return true;
  }
  // if (!paramDueDateData) {
  //   return true;
  // }
  if (!task.due_date) {
    return false;
  }

  // type dueDateData = | Date | {timePeriods: "Days" | "Weeks" | "Months" | null; timeUnit: number | null;} | { fromDate: string | null; toDate: string | null;}| null;
  // type DateType = "Any Date" | "Specific Date" | "Yesterday" | "Today" | "Tomorrow" | "Within the last" | "Within the next" | "Date Range" | null

  const taskDueDate = getNewDayDate(task.due_date);
  if (!taskDueDate) {
    return false;
  }
  const currentDayDate = getNewDayDate(new Date());
  if (!currentDayDate) {
    return false;
  }

  // paramDueDateType: "Any Date" | "Specific Date" | "Yesterday" | "Today" | "Tomorrow" | "Within the last" | "Within the next" | "Date Range" | null
  if (paramDueDateType === "Any Date" || paramDueDateType === null) {
    {
      return true;
    }
  }

  if (paramDueDateType === "Yesterday") {
    return taskDueDate.getTime() === subDays(currentDayDate, 1).getTime();
  }
  if (paramDueDateType === "Today") {
    return taskDueDate.getTime() === currentDayDate.getTime();
  }
  if (paramDueDateType === "Tomorrow") {
    return taskDueDate.getTime() === addDays(currentDayDate, 1).getTime();
  }
  if (paramDueDateType === "Specific Date") {
    if (!paramDueDateData) {
      return false;
    }
    const specificDate = getNewDayDate(paramDueDateData as Date);
    if (!specificDate) {
      return false;
    }
    return taskDueDate.getTime() === specificDate.getTime();
  }
  if (paramDueDateType === "Within the last") {
    if (!paramDueDateData) {
      return false;
    }
    const parsedDateData = WithinDateSchema.safeParse(paramDueDateData);
    if (!parsedDateData.success) {
      console.error("Invalid date data:", parsedDateData.error);
      {
        return false;
      }
    }
    const { timePeriods, timeUnit } = parsedDateData.data;
    if (!timeUnit || !timePeriods) {
      return false;
    }
    const dateRange =
      timePeriods === "Days"
        ? subDays(currentDayDate, timeUnit)
        : timePeriods === "Weeks"
          ? subWeeks(currentDayDate, timeUnit)
          : timePeriods === "Months"
            ? subMonths(currentDayDate, timeUnit)
            : undefined;
    if (!dateRange) {
      return false;
    }
    return taskDueDate.getTime() >= dateRange.getTime();
  }
  if (paramDueDateType === "Within the next") {
    console.log("Within the next", paramDueDateData);
    if (!paramDueDateData) {
      return false;
    }
    const parsedDateData = WithinDateSchema.safeParse(paramDueDateData);
    if (!parsedDateData.success) {
      console.error("Invalid date data:", parsedDateData.error);
      {
        return false;
      }
    }
    const { timePeriods, timeUnit } = parsedDateData.data;
    if (!timeUnit || !timePeriods) {
      return false;
    }
    const dateRange =
      timePeriods === "Days"
        ? addDays(currentDayDate, timeUnit)
        : timePeriods === "Weeks"
          ? addWeeks(currentDayDate, timeUnit)
          : timePeriods === "Months"
            ? addMonths(currentDayDate, timeUnit)
            : undefined;
    if (!dateRange) {
      return false;
    }
    console.log("dateRange", dateRange);
    console.log(taskDueDate.getTime(), dateRange.getTime());
    return (
      taskDueDate.getTime() >= currentDayDate.getTime() &&
      taskDueDate.getTime() <= dateRange.getTime()
    );
  }
  if (paramDueDateType === "Date Range") {
    if (!paramDueDateData) {
      return false;
    }
    const parsedDateData = DateRangeSearchSchema.safeParse(paramDueDateData);
    if (!parsedDateData.success) {
      console.error("Invalid date data:", parsedDateData.error);
      {
        return false;
      }
    }
    const { fromDate, toDate } = parsedDateData.data;
    if (!fromDate || !toDate) {
      return false;
    }
    const from = getNewDayDate(fromDate);
    const to = getNewDayDate(toDate);
    if (!from || !to) {
      return false;
    }
    return (
      taskDueDate.getTime() >= from.getTime() &&
      taskDueDate.getTime() <= to.getTime()
    );
  }

  if (paramDueDateType === "Overdue") {
    return taskDueDate.getTime() < currentDayDate.getTime();
  }

  console.error("Unknown due date type:", paramDueDateType);
  {
    return false;
  }
}
// #endregion filterSearchDueDate

// #region filterSearchCreatedDate
export function filterSearchCreatedDate(
  paramCreatedDateData: AllURLSearchFilterValues["createdDateData"],
  paramCreatedDateType: AllURLSearchFilterValues["createdDateType"],
  task: SearchTaskAdvanced
): boolean {
  if (!paramCreatedDateType) {
    return true;
  }
  if (!paramCreatedDateData) {
    return true;
  }
  if (!task.created_at) {
    return false;
  }

  // type createdDateData = | Date | {timePeriods: "Days" | "Weeks" | "Months" | null; timeUnit: number | null;} | { fromDate: string | null; toDate: string | null;}| null;
  // type DateType = "Any Date" | "Specific Date" | "Yesterday" | "Today" | "Tomorrow" | "Within the last" | "Within the next" | "Date Range" | null

  const taskCreatedDate = getNewDayDate(task.created_at);
  if (!taskCreatedDate) {
    return false;
  }
  const currentDayDate = getNewDayDate(new Date());
  if (!currentDayDate) {
    return false;
  }

  // paramCreatedDateType: "Any Date" | "Specific Date" | "Yesterday" | "Today" | "Tomorrow" | "Within the last" | "Within the next" | "Date Range" | null
  if (paramCreatedDateType === "Any Date" || paramCreatedDateType === null) {
    {
      return true;
    }
  }

  if (paramCreatedDateType === "Overdue") {
    console.error("Overdue is not a valid created date type");
    Swal.fire({
      icon: "error",
      title: "Overdue is not a valid created date type",
      text: "Please check your filters.",
    });
    {
      return false;
    }
  }

  if (paramCreatedDateType === "Yesterday") {
    return taskCreatedDate.getTime() === subDays(currentDayDate, 1).getTime();
  }
  if (paramCreatedDateType === "Today") {
    return taskCreatedDate.getTime() === currentDayDate.getTime();
  }
  if (paramCreatedDateType === "Tomorrow") {
    return taskCreatedDate.getTime() === addDays(currentDayDate, 1).getTime();
  }
  if (paramCreatedDateType === "Specific Date") {
    if (!paramCreatedDateData) {
      return false;
    }
    const specificDate = getNewDayDate(paramCreatedDateData as Date);
    if (!specificDate) {
      return false;
    }
    return taskCreatedDate.getTime() === specificDate.getTime();
  }
  if (paramCreatedDateType === "Within the last") {
    if (!paramCreatedDateData) {
      return false;
    }
    const parsedDateData = WithinDateSchema.safeParse(paramCreatedDateData);
    if (!parsedDateData.success) {
      console.error("Invalid date data:", parsedDateData.error);
      {
        return false;
      }
    }
    const { timePeriods, timeUnit } = parsedDateData.data;
    if (!timeUnit || !timePeriods) {
      return false;
    }
    const dateRange =
      timePeriods === "Days"
        ? subDays(currentDayDate, timeUnit)
        : timePeriods === "Weeks"
          ? subWeeks(currentDayDate, timeUnit)
          : timePeriods === "Months"
            ? subMonths(currentDayDate, timeUnit)
            : undefined;
    if (!dateRange) {
      return false;
    }
    return taskCreatedDate.getTime() >= dateRange.getTime();
  }
  if (paramCreatedDateType === "Within the next") {
    if (!paramCreatedDateData) {
      return false;
    }
    const parsedDateData = WithinDateSchema.safeParse(paramCreatedDateData);
    if (!parsedDateData.success) {
      console.error("Invalid date data:", parsedDateData.error);
      {
        return false;
      }
    }
    const { timePeriods, timeUnit } = parsedDateData.data;
    if (!timeUnit || !timePeriods) {
      return false;
    }
    const dateRange =
      timePeriods === "Days"
        ? addDays(currentDayDate, timeUnit)
        : timePeriods === "Weeks"
          ? addWeeks(currentDayDate, timeUnit)
          : timePeriods === "Months"
            ? addMonths(currentDayDate, timeUnit)
            : undefined;
    if (!dateRange) {
      return false;
    }
    return (
      taskCreatedDate.getTime() >= currentDayDate.getTime() &&
      taskCreatedDate.getTime() <= dateRange.getTime()
    );
  }
  if (paramCreatedDateType === "Date Range") {
    if (!paramCreatedDateData) {
      return false;
    }
    const parsedDateData =
      DateRangeSearchSchema.safeParse(paramCreatedDateData);
    if (!parsedDateData.success) {
      console.error("Invalid date data:", parsedDateData.error);
      {
        return false;
      }
    }
    const { fromDate, toDate } = parsedDateData.data;
    if (!fromDate || !toDate) {
      return false;
    }
    const from = getNewDayDate(fromDate);
    const to = getNewDayDate(toDate);
    if (!from || !to) {
      return false;
    }
    return (
      taskCreatedDate.getTime() >= from.getTime() &&
      taskCreatedDate.getTime() <= to.getTime()
    );
  }
  console.error("Unknown created date type:", paramCreatedDateType);
  {
    return false;
  }
}
// #endregion filterSearchCreatedDate

// #region filterSearchModifiedDate
export function filterSearchModifiedDate(
  paramModifiedDateData: AllURLSearchFilterValues["modifiedDateData"],
  paramModifiedDateType: AllURLSearchFilterValues["modifiedDateType"],
  task: SearchTaskAdvanced
): boolean {
  if (!paramModifiedDateType) {
    return true;
  }
  if (!paramModifiedDateData) {
    return true;
  }
  if (!task.updated_at) {
    return false;
  }

  // type modifiedDateData = | Date | {timePeriods: "Days" | "Weeks" | "Months" | null; timeUnit: number | null;} | { fromDate: string | null; toDate: string | null;}| null;
  // type DateType = "Any Date" | "Specific Date" | "Yesterday" | "Today" | "Tomorrow" | "Within the last" | "Within the next" | "Date Range" | null

  const taskModifiedDate = getNewDayDate(task.updated_at);
  if (!taskModifiedDate) {
    return false;
  }
  const currentDayDate = getNewDayDate(new Date());
  if (!currentDayDate) {
    return false;
  }

  // paramModifiedDateType: "Any Date" | "Specific Date" | "Yesterday" | "Today" | "Tomorrow" | "Within the last" | "Within the next" | "Date Range" | null
  if (paramModifiedDateType === "Any Date") {
    {
      return true;
    }
  }

  if (paramModifiedDateType === "Yesterday") {
    return taskModifiedDate.getTime() === subDays(currentDayDate, 1).getTime();
  }
  if (paramModifiedDateType === "Today") {
    return taskModifiedDate.getTime() === currentDayDate.getTime();
  }
  if (paramModifiedDateType === "Tomorrow") {
    return taskModifiedDate.getTime() === addDays(currentDayDate, 1).getTime();
  }
  if (paramModifiedDateType === "Specific Date") {
    if (!paramModifiedDateData) {
      return false;
    }
    const specificDate = getNewDayDate(paramModifiedDateData as Date);
    if (!specificDate) {
      return false;
    }
    return taskModifiedDate.getTime() === specificDate.getTime();
  }
  if (paramModifiedDateType === "Within the last") {
    if (!paramModifiedDateData) {
      return false;
    }
    const parsedDateData = WithinDateSchema.safeParse(paramModifiedDateData);
    if (!parsedDateData.success) {
      console.error("Invalid date data:", parsedDateData.error);
      {
        return false;
      }
    }
    const { timePeriods, timeUnit } = parsedDateData.data;
    if (!timeUnit || !timePeriods) {
      return false;
    }
    const dateRange =
      timePeriods === "Days"
        ? subDays(currentDayDate, timeUnit)
        : timePeriods === "Weeks"
          ? subWeeks(currentDayDate, timeUnit)
          : timePeriods === "Months"
            ? subMonths(currentDayDate, timeUnit)
            : undefined;
    if (!dateRange) {
      return false;
    }
    return taskModifiedDate.getTime() >= dateRange.getTime();
  }
  if (paramModifiedDateType === "Within the next") {
    //  cannot be modified in the future
    {
      return false;
    }
  }
  if (paramModifiedDateType === "Date Range") {
    if (!paramModifiedDateData) {
      return false;
    }
    const parsedDateData = DateRangeSearchSchema.safeParse(
      paramModifiedDateData
    );
    if (!parsedDateData.success) {
      console.error("Invalid date data:", parsedDateData.error);
      {
        return false;
      }
    }
    const { fromDate, toDate } = parsedDateData.data;
    if (!fromDate || !toDate) {
      return false;
    }
    const from = getNewDayDate(fromDate);
    const to = getNewDayDate(toDate);
    if (!from || !to) {
      return false;
    }
    return (
      taskModifiedDate.getTime() >= from.getTime() &&
      taskModifiedDate.getTime() <= to.getTime()
    );
  }
  console.error("Unknown modified date type:", paramModifiedDateType);
  {
    return false;
  }
}
// #endregion filterSearchModifiedDate

// #region filterSearchCompletedDate
export function filterSearchCompletedDate(
  paramCompletedDateData: AllURLSearchFilterValues["completedDateData"],
  paramCompletedDateType: AllURLSearchFilterValues["completedDateType"],
  task: SearchTaskAdvanced
): boolean {
  if (!paramCompletedDateType) {
    return true;
  }
  if (!paramCompletedDateData) {
    return true;
  }
  const taskHistory: SingleAuditLog[] = task.history;
  if (!taskHistory || taskHistory.length === 0) {
    return false;
  }
  // find all history items with type "update" and field "status"
  const completedHistory = taskHistory.filter(
    (history) =>
      history.type === "update" &&
      history.changes.some((change) => change.field === "status")
  ) as SingleAuditLogUpdate[];
  if (!completedHistory || completedHistory.length === 0) {
    return false;
  }

  const sortedCompletedHistory = completedHistory.sort((a, b) => {
    const dateA = new Date(a.timestamp);
    const dateB = new Date(b.timestamp);
    return dateA.getTime() - dateB.getTime();
  });

  const lastStatusChangeHistory =
    sortedCompletedHistory[sortedCompletedHistory.length - 1];
  if (!lastStatusChangeHistory) {
    return false;
  }
  const lastStatusChange = lastStatusChangeHistory.changes.find(
    (change) => change.field === "status"
  );
  if (!lastStatusChange) {
    return false;
  }
  if (!lastStatusChange.new_value) {
    return false;
  }
  if (lastStatusChange.new_value !== "complete") {
    return false;
  }
  const completedDate = getNewDayDate(lastStatusChangeHistory.timestamp);
  if (!completedDate) {
    return false;
  }
  const currentDayDate = getNewDayDate(new Date());
  if (!currentDayDate) {
    return false;
  }
  // paramCompletedDateType: "Any Date" | "Specific Date" | "Yesterday" | "Today" | "Tomorrow" | "Within the last" | "Within the next" | "Date Range" | null

  if (paramCompletedDateType === "Any Date") {
    {
      return true;
    }
  }
  if (paramCompletedDateType === "Yesterday") {
    return completedDate.getTime() === subDays(currentDayDate, 1).getTime();
  }
  if (paramCompletedDateType === "Today") {
    return completedDate.getTime() === currentDayDate.getTime();
  }
  if (paramCompletedDateType === "Tomorrow") {
    return completedDate.getTime() === addDays(currentDayDate, 1).getTime();
  }
  if (paramCompletedDateType === "Specific Date") {
    if (!paramCompletedDateData) {
      return false;
    }
    const specificDate = getNewDayDate(paramCompletedDateData as Date);
    if (!specificDate) {
      return false;
    }
    return completedDate.getTime() === specificDate.getTime();
  }
  if (paramCompletedDateType === "Within the last") {
    if (!paramCompletedDateData) {
      return false;
    }
    const parsedDateData = WithinDateSchema.safeParse(paramCompletedDateData);
    if (!parsedDateData.success) {
      console.error("Invalid date data:", parsedDateData.error);
      {
        return false;
      }
    }
    const { timePeriods, timeUnit } = parsedDateData.data;
    if (!timeUnit || !timePeriods) {
      return false;
    }
    const dateRange =
      timePeriods === "Days"
        ? subDays(currentDayDate, timeUnit)
        : timePeriods === "Weeks"
          ? subWeeks(currentDayDate, timeUnit)
          : timePeriods === "Months"
            ? subMonths(currentDayDate, timeUnit)
            : undefined;
    if (!dateRange) {
      return false;
    }
    return completedDate.getTime() >= dateRange.getTime();
  }
  if (paramCompletedDateType === "Within the next") {
    // cannot be completed in the future
    {
      return false;
    }
  }
  if (paramCompletedDateType === "Date Range") {
    if (!paramCompletedDateData) {
      return false;
    }
    const parsedDateData = DateRangeSearchSchema.safeParse(
      paramCompletedDateData
    );
    if (!parsedDateData.success) {
      console.error("Invalid date data:", parsedDateData.error);
      {
        return false;
      }
    }
    const { fromDate, toDate } = parsedDateData.data;
    if (!fromDate || !toDate) {
      return false;
    }
    const from = getNewDayDate(fromDate);
    const to = getNewDayDate(toDate);
    if (!from || !to) {
      return false;
    }
    return (
      completedDate.getTime() >= from.getTime() &&
      completedDate.getTime() <= to.getTime()
    );
  }
  console.error("Unknown completed date type:", paramCompletedDateType);
  {
    return false;
  }
}
// #endregion filterSearchCompletedDate

// #endregion filterSearch
